<template>
    <div class="">

        <div v-if="iframeIsLoading" class="d-flex justify-content-center mt-5 mb-1">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <!-- The form moves on by itself after a few seconds; text-center keeps a wrapped message centred. -->
        <div v-if="maxRetryReached" class="text-center mt-5 mb-1">
            <h4>{{ getTranslatedText('__max_retry_reached__', 'components.flinks') }}</h4>
            <ContinuingNotice />
        </div>

        <iframe ref="flinksFrame" class="flinksconnect" :height="iframeHeight" :style="iframeStyle" loading="lazy" :src="iframeString" @load="onFrameLoad"></iframe>

    </div>
</template>
<script setup>

import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { formFunctions } from '@/form/helpers/functions.js';
import { meetsPartnerCriteria } from '@/utils/partner-eligibility.js';
import { delayedCall } from '@/utils/timing.js';
import { acceptsFlinksMessage, flinksOrigin, iframeFlinksRedirectParams, iframeLeadToken, isIframe } from '@/form/helpers/iframe-config.js';
import ContinuingNotice from '@/components/form/ContinuingNotice.vue';

const props = defineProps({
    addressProp: {
        type: Object,
        required: false,
        default: null,
    },
    productName: {
        type: String,
        required: false,
        default: 'personal_loan',
    }
});

const emit = defineEmits(["update:model-value", "input", "goToNextStep"]);

const { app, updateApplicationField, getTranslatedText } = inject('app');

// --- Refs ---

const isTestFlow = false;
const isTestIframe = false;
const iframeIsLoading = ref(false);
const maxRetryReached = ref(false);
const flinksFrame = ref(null);

const province = props.addressProp?.province ?? null;
let daysOfTransactions = 90;
if (province && province === 'QC' && props.productName === 'personal_loan') {
    daysOfTransactions = 365;
}

// --- Computed ---

const iframeString = computed(() => {
    return `https://loanscanada-iframe.private.fin.ag/v2/?demo=${showDemoIframe.value}&termsUrl=https%3A%2F%2Floanscanada.ca%2Fterms%2F&terms&NoCheckbox=true&termsTextLink=Terms+and+Conditions&termsTextPreLink=By+choosing+to+continue%2C+you+agree+to+the&termsTextPostLink=and+authorize+the+communication+of+your+financial+information+to+Loans+Canada and its lender partners.&customerName=Loans+Canada&accountSelectorEnable=true&accountSelectorCurrency=cad&maximumRetry=3&daysOfTransactions=Days${daysOfTransactions}${iframeFlinksRedirectParams()}`;
});

const iframeHeight = computed(() => {
    return iframeIsLoading.value ? '0' : '760';
});

const iframeStyle = computed(() => {
    return iframeIsLoading.value ? '' : 'width:100%';
});

const shouldSkipFlinks = computed(() => {
    return (isTestFlow || (app.value && app.value.flinks_demo && app.value.flinks_demo === '1')) ? false :
    (
        !app.value ||
        (app.value.flinks_skip && app.value.flinks_skip === '1') ||
        !app.value.unique_id ||
        // Embedded, a login can only be stored against the signed lead token;
        // without one the connection would be thrown away, so don't ask for it.
        (isIframe() && !iframeLeadToken()) ||
        !appMeetsConditions.value
    );
});

const showDemoIframe = computed(() => {
    return isTestIframe || (app.value && app.value.flinks_demo && app.value.flinks_demo === '1');
});

const appMeetsConditions = computed(() => {
    const appProductName = app.value.product_name;
    let passedFiltersBasedInProduct = false;
    let passedPartnerCriteria = false;
    const passedGeneralFilters = app.value.address
        && app.value.address.province
        && app.value.income_type
        && app.value.monthly_income;

    if (passedGeneralFilters) {
        switch (appProductName) {
            case 'car_loan':
            case 'powersports':
                passedFiltersBasedInProduct = true;
                break;
            default:
            case 'personal_loan':
                passedFiltersBasedInProduct = app.value.direct_deposit
                    && app.value.direct_deposit === 'yes';
                break;
        }
    }

    if (passedGeneralFilters && passedFiltersBasedInProduct) {
        passedPartnerCriteria = meetsPartnerCriteria('skcp', app.value)
            || meetsPartnerCriteria('gl', app.value)
            || meetsPartnerCriteria('lnie', app.value)
            || meetsPartnerCriteria('cmcr', app.value);
    }

    return passedGeneralFilters && passedFiltersBasedInProduct && passedPartnerCriteria;
});

// --- Methods ---

async function checkIfFlinksExists() {
    if (!app.value || !app.value.unique_id) {
        return false;
    }

    const payload = {
        uid: app.value.unique_id
    };

    const response = await formFunctions.apiHasFlinks(payload);
    // Skip the Flinks iframe if the lead already has a recent log (has_flinks_log) OR
    // is under a rule-based block (is_blocked, true only when CMI is in enforce mode).
    if (response?.status === "success" && (response?.has_flinks_log || response?.is_blocked)) {
        return true;
    }
    return false;
}

async function messageEvent(e) {
    if (!acceptsFlinksMessage(e, { flinksOrigin: flinksOrigin(iframeString.value), frameWindow: flinksFrame.value?.contentWindow ?? null })) {
        return;
    }
    if (e && e.data && e.data.step) {
        switch (e.data.step) {
            case "APP_MOUNTED":
                iframeIsLoading.value = false;
                break;
            case "REDIRECT":

                if (typeof e.data.loginId !== "undefined") {

                    updateApplicationField('completed_flinks', 'yes');

                    const payload = {
                        flinksLoginId: e.data.loginId,
                        uid: app.value.unique_id
                    };

                    const response = await formFunctions.apiStoreFlinks(payload);

                    if (typeof response.status !== "undefined") {
                        if (response.status !== "success") {
                            console.warn("Flinks: failed to store login id");
                        }
                    } else {
                        console.warn("Flinks: unexpected response format");
                    }
                }

                nextStepAfterSeconds(2);

                break;
            case "MAXIMUM_RETRY_REACHED":
                maxRetryReached.value = true;
                nextStepAfterSeconds(3);
                break;
            default:
                break;
        }

    }
}

// Embedded, Flinks' APP_MOUNTED goes to the partner's page (window.top), not
// to us, so the frame's own load event ends the spinner instead.
function onFrameLoad() {
    if (isIframe()) {
        iframeIsLoading.value = false;
    }
}

function nextStepAfterSeconds(seconds = 2) {
    delayedCall(() => emit('goToNextStep'), seconds);
}

// --- Lifecycle ---

onMounted(async () => {
    window.addEventListener("message", messageEvent, false);
    iframeIsLoading.value = true;
    if (shouldSkipFlinks.value || await checkIfFlinksExists()) {
        emit('goToNextStep');
    }
});

onBeforeUnmount(() => {
    window.removeEventListener("message", messageEvent, false);
});
</script>


<style scoped>
</style>
